import _ from "lodash";
import type React from "react";
import { ClipboardEvent, useEffect } from "react";

import { useRef, useState } from "react";
import { twMerge } from "tailwind-merge";
import { useDebounceCallback } from "usehooks-ts";
import { defaultInputClassName } from "./Input.react";
import { useOrigin } from "@/features/flow/hooks/use-origin";
import { FromURL } from "@/features/flow/types";
import { useNativeInputs } from "@/features/utils/is-mobile";

interface CodeInputProps extends Omit<React.ComponentPropsWithoutRef<"input">, "onChange"> {
  label?: string;
  inputComponent?: React.ReactNode;
  inputClassName?: string;
  className?: string;
  codeNumberOfCharacters?: number;
  onChange?: (code: string) => void;
  focus?: boolean;
  loading?: boolean;
  dataCy?: string;
}

export default function CodeInput({
  label,
  inputComponent,
  inputClassName,
  className,
  codeNumberOfCharacters,
  onChange,
  focus,
  loading,
  dataCy,
  ...props
}: CodeInputProps) {
  const origin = useOrigin();
  const [inputContent, setInputContent] = useState<string[]>(
    Array.from({ length: codeNumberOfCharacters || 1 }, () => "")
  );
  const isMobile = useNativeInputs();

  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const debouncedOnChange = useDebounceCallback((code: string) => onChange && onChange(code), 100);

  // Calculate output value
  const calculateRealValue = (inputContent: string[]) => inputContent.join("");

  useEffect(() => {
    if (focus && inputRefs.current) {
      inputRefs.current[0]?.focus();
    }
  }, [focus]);

  const handleInputChange = (index: number, value: string) => {
    setInputContent((inputContent) => {
      const newInputContent = [...inputContent];
      newInputContent[index] = value;

      // Send callback to atom user
      if (
        debouncedOnChange &&
        calculateRealValue(newInputContent).length === (codeNumberOfCharacters || 1)
      ) {
        const realValue = calculateRealValue(newInputContent);
        debouncedOnChange(realValue);
      }

      return newInputContent;
    });

    if (value !== "" && index < inputRefs.current.length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };
  const handleInputPaste = async (event: ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    if (isMobile) {
      const clipboardText = await navigator.clipboard.readText();
      const pastedText =
        clipboardText.length > 0 ? clipboardText : event.clipboardData.getData("text");
      pastedText
        .split("")
        .filter((_char, index) => index < inputContent.length)
        .forEach((char, index) => {
          handleInputChange(index, char);
          inputRefs?.current[inputRefs.current.length - 1]?.focus();
        });
    }
  };

  const handleKeyDown = (index: number, event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Backspace" && inputContent[index] === "" && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }

    if (event.key === "ArrowLeft" && index > 0) {
      event.preventDefault();
      const input = inputRefs.current[index - 1];

      // Set cursor at the end of input
      if (input) {
        input.focus();
        input.setSelectionRange(input.value.length, input.value.length);
      }
    }

    if (event.key === "ArrowRight" && index < inputContent.length) {
      event.preventDefault();
      const input = inputRefs.current[index + 1];

      // Set cursor at the end of input
      if (input) {
        input.focus();
        input.setSelectionRange(input.value.length, input.value.length);
      }
    }

    // Handle Ctr + V
    if ((event.ctrlKey || event.metaKey) && event.code === "KeyV") {
      event.preventDefault();

      // Read text from clipboard
      navigator.clipboard.readText().then((clipText) =>
        clipText
          .split("")
          .filter((_char, index) => index < inputContent.length)
          .forEach((char, index) => {
            handleInputChange(index, char);
            inputRefs?.current[inputRefs.current.length - 1]?.focus();
          })
      );
    }
  };
  return (
    <>
      <div
        className={twMerge(
          "flex items-center space-x-0 lg:space-x-2" + " " + className,
          loading ? "opacity-50" : ""
        )}
      >
        {codeNumberOfCharacters &&
          inputContent.map((value, index) => (
            <input
              data-cy={dataCy}
              key={index}
              type="number"
              pattern="\d*"
              value={value}
              required
              maxLength={1}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                if (event.target.value.length > 1) {
                  return event.target.value
                    .split("")
                    .filter((_char, index) => index < inputContent.length)
                    .forEach((char, index) => {
                      handleInputChange(index, char);
                      inputRefs?.current[inputRefs.current.length - 1]?.focus();
                    });
                } else handleInputChange(index, event.target.value);
              }}
              onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) =>
                handleKeyDown(index, event)
              }
              onPaste={handleInputPaste}
              placeholder="•"
              className={twMerge(
                "w-10 max-w-11 text-center",
                defaultInputClassName,
                inputClassName,
                "rounded-none first:rounded-l-sm last:rounded-r-sm lg:rounded-sm",
                "px-0",
                origin !== FromURL.None && "focus:border-primary"
              )}
              ref={(ref: HTMLInputElement | null) => {
                inputRefs.current[index] = ref;
              }}
              {..._.omit(
                props,
                "label",
                "inputClassName",
                "className",
                "codeNumberOfCharacters",
                "onChange",
                "loading",
                "focus"
              )}
            />
          ))}
      </div>
    </>
  );
}
